import React, { useContext, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  LocationCity,
  Cake,
  School,
  QrCode2,
  Download,
  Edit,
  CalendarToday
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import StudentBottomNav from '../components/StudentBottomNav';
import GradientSurface from '../components/shared/GradientSurface';
import { GRADIENTS, RADIUS } from '../theme/tokens';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Professional Profile Page with modern design
 */
function ProfilePage() {
  const { user } = useContext(AuthContext);
  const [qrDownloading, setQrDownloading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
  const qrUrl = user?.qr_url ? `${API_BASE}${user.qr_url}` : null;

  const handleDownloadQR = () => {
    if (!qrUrl) return;
    setQrDownloading(true);

    // Create a temporary link to download the QR code
    const link = document.createElement('a');
    link.href = qrUrl;
    link.download = `${user?.name}-QR-Code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => setQrDownloading(false), 1000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const profileFields = [
    {
      icon: <Email color="primary" />,
      label: 'Email',
      value: user?.email,
      show: true
    },
    {
      icon: <Phone color="primary" />,
      label: 'Phone Number',
      value: user?.phone,
      show: user?.phone
    },
    {
      icon: <Cake color="primary" />,
      label: 'Age',
      value: user?.age ? `${user.age} years` : null,
      show: user?.age
    },
    {
      icon: <LocationCity color="primary" />,
      label: 'City',
      value: user?.city,
      show: user?.city
    },
    {
      icon: <School color="primary" />,
      label: 'Interested Courses',
      value: user?.interested_courses,
      show: user?.interested_courses
    },
    {
      icon: <CalendarToday color="primary" />,
      label: 'Member Since',
      value: user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) : null,
      show: user?.created_at
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: 4,
        pb: 10,
        bgcolor: 'transparent'
      }}
    >
      <Container maxWidth="sm">
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <MotionBox variants={itemVariants} mb={4}>
            <GradientSurface variant="brand" radius={RADIUS.xl} sx={{ p: 4, color: 'white' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box display="flex" alignItems="center" gap={3}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'background.paper',
                      color: 'primary.main',
                      fontSize: '2rem',
                      fontWeight: 700
                    }}
                  >
                    {user?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="800" gutterBottom>
                      {user?.name}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        label={user?.role?.toUpperCase()}
                        sx={{
                          bgcolor: 'background.paper',
                          color: 'primary.main',
                          fontWeight: 600
                        }}
                      />
                      {user?.city && (
                        <Chip
                          label={user.city}
                          variant="outlined"
                          sx={{
                            borderColor: 'white',
                            color: 'white'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Tooltip title="Edit Profile">
                  <IconButton
                    sx={{
                      bgcolor: 'background.paper',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)'
                      }
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Box>
            </GradientSurface>
          </MotionBox>

          <Grid container spacing={3}>
            {/* Profile Information Card */}
            <Grid item xs={12} md={8}>
              <MotionPaper
                variants={itemVariants}
                sx={{ borderRadius: RADIUS.xl, p: 4 }}
              >
                <Typography variant="h5" fontWeight="700" gutterBottom mb={3}>
                  Profile Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  {profileFields.filter(field => field.show).map((field, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          p: 2,
                          borderRadius: 3,
                          bgcolor: 'action.hover',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: 'action.selected',
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {field.icon}
                        </Box>
                        <Box flex={1}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight="600"
                            textTransform="uppercase"
                            display="block"
                            mb={0.5}
                          >
                            {field.label}
                          </Typography>
                          <Typography variant="body1" fontWeight="500">
                            {field.value || 'Not provided'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box mt={4}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Edit />}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Edit Profile
                  </Button>
                </Box>
              </MotionPaper>
            </Grid>

            {/* QR Code Card */}
            <Grid item xs={12} md={4}>
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: RADIUS.xl,
                  background: GRADIENTS.brand,
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <QrCode2 sx={{ fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" fontWeight="600">
                        Your Digital Pass
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Scan at library entrance
                      </Typography>
                    </Box>
                  </Box>

                  {qrUrl ? (
                    <>
                      <Box
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: 3,
                          p: 2,
                          mb: 2,
                          textAlign: 'center'
                        }}
                      >
                        <MotionBox
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.3 }}
                        >
                          <img
                            src={qrUrl}
                            alt="Your QR Code"
                            style={{
                              maxWidth: '100%',
                              height: 'auto',
                              borderRadius: '12px'
                            }}
                          />
                          <Typography
                            variant="caption"
                            display="block"
                            mt={1}
                            color="text.secondary"
                            fontWeight="600"
                          >
                            QR ID: {user?.static_qr_id}
                          </Typography>
                        </MotionBox>
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Download />}
                        onClick={handleDownloadQR}
                        disabled={qrDownloading}
                        sx={{
                          bgcolor: 'background.paper',
                          color: 'primary.main',
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.9)'
                          }
                        }}
                      >
                        {qrDownloading ? 'Downloading...' : 'Download QR Code'}
                      </Button>
                    </>
                  ) : (
                    <Box
                      textAlign="center"
                      py={4}
                      bgcolor="rgba(255,255,255,0.1)"
                      borderRadius={3}
                    >
                      <QrCode2 sx={{ fontSize: 64, opacity: 0.5, mb: 2 }} />
                      <Typography>QR code not available</Typography>
                    </Box>
                  )}
                </CardContent>
              </MotionCard>

              {/* Quick Stats */}
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: RADIUS.xl,
                  mt: 3,
                  background: GRADIENTS.coin,
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight="600" mb={2}>
                    Account Status
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Account Type
                      </Typography>
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          bgcolor: 'background.paper',
                          color: 'success.main',
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Total Bookings
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        0
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Active Sessions
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        0
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          </Grid>
        </MotionBox>
      </Container>

      <StudentBottomNav />
    </Box>
  );
}

export default ProfilePage;
